/**
 * =============================================================================
 * EL IDIOMA QUE SE ELIGE EN LA LANDING PASA AL PORTAL
 * =============================================================================
 * La landing y el portal tienen dos sistemas de idioma distintos, y por buenas
 * razones: la landing es texto de marketing con su propio diccionario a mano
 * (ES/EN/FR) y el portal es i18next con ficheros por area. No se van a
 * fusionar.
 *
 * Lo que si tiene sentido es que la eleccion viaje: quien entra en la web en
 * frances y hace login espera encontrarse el portal en frances, no volver al
 * castellano de golpe.
 *
 * TRES REGLAS, Y LAS TRES IMPORTAN:
 *
 * 1. **Solo viaja un idioma que el portal tenga ENCENDIDO.** Es la clave: hoy
 *    el frances esta apagado a la espera de revision nativa, asi que elegir FR
 *    en la landing cambia la landing y no toca el portal. El dia que se
 *    encienda —una linea en `config.js`— esto empieza a funcionar solo, sin
 *    tocar nada mas.
 *
 * 2. **Un idioma que el portal no tiene NO lo devuelve al castellano.** La
 *    landing ofrece ingles y el portal no. Si alguien con el portal en frances
 *    pincha EN en la web, lo que NO puede pasar es que su portal se le quede en
 *    castellano: no ha pedido eso. Se deja como estaba.
 *
 *    Ojo con esto, porque no es teorico: `setLocale` pasa por
 *    `resolveInitialLanguage`, que devuelve el idioma por defecto para
 *    cualquier cosa que no reconozca. Llamarlo sin el guardia de arriba
 *    convertiria "elegir ingles" en "resetear el portal a castellano".
 *
 * 3. **Solo viaja una eleccion DELIBERADA.** La landing tambien adivina el
 *    idioma del navegador la primera vez; eso no propaga. Un navegador en
 *    frances no debe cambiarle el portal a nadie sin que lo haya pedido.
 *
 * Y desde que el portal tiene su propio selector, tambien al reves: quien pone
 * el portal en castellano y despues visita la web publica no deberia
 * encontrarsela en frances. Esa direccion es mas simple —la landing tiene los
 * dos idiomas del portal y uno mas— asi que solo hay que escribir su clave.
 * =============================================================================
 */

import { isLanguageEnabled } from "./config";
import { useLocaleStore } from "../store/localeStore";
import { LANDING_STORAGE_KEY } from "../pages/Public/LandingPage/v2/i18n/LandingLangContext";

/**
 * @param {string} codigo idioma elegido en la landing ("es" | "en" | "fr")
 * @returns {boolean} true solo si el portal se ha quedado en ese idioma
 */
export function propagarIdiomaAlPortal(codigo) {
  // Regla 1 y 2: si el portal no lo tiene encendido, no se toca nada.
  if (!isLanguageEnabled(codigo)) return false;

  const { locale, setLocale } = useLocaleStore.getState();
  if (locale === codigo) return false;

  setLocale(codigo);
  return true;
}

/**
 * El camino de vuelta: portal -> landing.
 *
 * Solo escribe en almacenamiento; la landing lo lee cuando se monta, que es
 * cuando toca. No hay nada que avisar porque las dos pantallas nunca estan
 * vivas a la vez.
 */
export function propagarIdiomaALaLanding(codigo) {
  if (!isLanguageEnabled(codigo)) return false;
  try {
    window.localStorage.setItem(LANDING_STORAGE_KEY, codigo);
    return true;
  } catch (_e) {
    // Navegacion privada con almacenamiento bloqueado: no es motivo para
    // romper un cambio de idioma que por lo demas ha funcionado.
    return false;
  }
}
